import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {
    useCreateDocumentsMutation,
    useGetDocumentsQuery,
    useReplaceDocumentMutation,
} from 'api/main';
import { useSendDistributorDocumentsMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { tunnelSchemas } from 'schemas';
import { docTypes, RoleNames } from 'utils/constants';
import {
    checkAcceptedFileTypes,
    checkImageFile,
    setTunnelChangesHandlers,
    validateFileSize,
} from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import TunnelBox from '../common/TunnelBox';
import TunnelStep from '../common/TunnelStep';
import Document from 'containers/subscriptions/Document';
import UploadDocument from 'containers/subscriptions/UploadDocument';
import { DocumentInput, ErrorMessage, Loader, SubmitButton } from 'components';
import { Box, Typography } from '@mui/material';

const DistributorDocuments = ({ userStep, handleNextStep }) => {
    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const { data: allDocuments = [], isLoading: documentsLoader } = useGetDocumentsQuery({
        belongsTo: internalUser.id,
        pagination: false,
    });
    const [createDocuments, { isSuccess: createSuccess }] = useCreateDocumentsMutation();
    const [replaceDocument, { isSuccess: replaceSuccess }] = useReplaceDocumentMutation();
    const [sendDistributorDocuments, { isLoading: buttonLoader }] =
        useSendDistributorDocumentsMutation();

    const [allowSubmit, setAllowSubmit] = useState(false);
    const [fileSizeError, setFileSizeError] = useState(null);
    const [loadingDoc, setLoadingDoc] = useState('');
    const isStepVerified = useMemo(() => !!userStep?.successfulAttemptsNumber, [userStep]);
    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const distributorCompany = useMemo(
        () => tunnelSubscription?.distributorCompany,
        [tunnelSubscription]
    );
    const role = useMemo(() => tunnelSubscription?.role?.name, [tunnelSubscription]);
    const userIsAdminDistributor = role === RoleNames.ADMIN_CIF;
    const bankingStatement = useMemo(
        () => tunnelSubscription?.bankingStatement,
        [tunnelSubscription]
    );
    const documents = useMemo(
        () => tunnelSchemas.initDistributorDocuments(distributorCompany, bankingStatement),
        [distributorCompany, bankingStatement]
    );

    const otherDocuments = useMemo(
        () =>
            allDocuments.filter(
                doc => doc.type === docTypes.OTHER && doc.createdBy?.id === internalUser.id
            ),
        [allDocuments, internalUser]
    );

    const defaultError = useMemo(
        () => ({
            legalRepresentantAuthority: false,
            oriasAttestation: false,
            criminalRecordExtract: false,
            kbis: false,
            insuranceCertificate: false,
            legalRepresentantId: false,
            signatoryId: false,
            rib: false,
            logo: false,
        }),
        []
    );

    const [errors, setErrors] = useState(defaultError);

    const findDocument = useCallback(
        fileType =>
            allDocuments.find(doc => doc.subtype === fileType) || documents[fileType] || null,
        [allDocuments, documents]
    );

    const formIsValid = useCallback(
        (shouldSetErrors = true) => {
            const [valid, validationErrors] = Object.keys(documents).reduce(
                (acc, current) => {
                    if (current !== 'logo' && !findDocument(current)) {
                        if (
                            (distributorCompany?.legalRepresentant &&
                                current === 'legalRepresentantAuthority') ||
                            (!userIsAdminDistributor && current === 'oriasAttestation') ||
                            (!userIsAdminDistributor && current === 'criminalRecordExtract') ||
                            (distributorCompany?.legalRepresentant &&
                                current === 'legalRepresentantId')
                        ) {
                            return acc;
                        }
                        return [false, { ...acc[1], [current]: true }];
                    }
                    return acc;
                },
                [true, {}]
            );
            if (shouldSetErrors) {
                setErrors(prevErrors => ({ ...prevErrors, ...validationErrors }));
            }
            return valid;
        },
        [documents, findDocument, userIsAdminDistributor, distributorCompany]
    );

    const handleUpload = useCallback(
        e => {
            const uploadedFile = e.target.files[0];
            const name = e.target.name;
            setFileSizeError(null);
            if (!validateFileSize(uploadedFile)) {
                setFileSizeError(name);
                return;
            }
            if (name === 'logo') {
                checkImageFile(uploadedFile, () => {
                    const formData = new FormData();
                    const existingDocument = findDocument(name);
                    if (existingDocument) {
                        //replace document
                        formData.append('file', uploadedFile);
                        setLoadingDoc('logo');
                        replaceDocument({ id: existingDocument.id, formData, updateUser: true });
                    } else {
                        //create new document
                        formData.append('files[]', uploadedFile);
                        formData.append('type', docTypes.COMPANY);
                        formData.append('subtype', name);
                        setLoadingDoc(name);
                        createDocuments({ formData, updateUser: true });
                    }
                    setErrors(defaultError);
                });
            } else {
                checkAcceptedFileTypes(uploadedFile, () => {
                    const formData = new FormData();
                    const existingDocument = findDocument(name);

                    setLoadingDoc(name);
                    if (existingDocument) {
                        //replace document
                        formData.append('file', uploadedFile);
                        replaceDocument({ id: existingDocument.id, formData, updateUser: true });
                    } else {
                        //create new document
                        formData.append('files[]', uploadedFile);
                        if (name === 'rib') {
                            formData.append('type', docTypes.RIB);
                        } else {
                            formData.append('type', docTypes.COMPANY);
                        }
                        formData.append('subtype', name);
                        createDocuments({ formData, updateUser: true });
                    }
                    setErrors(defaultError);
                });
            }
        },
        [defaultError, createDocuments, replaceDocument, findDocument]
    );

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            if (formIsValid()) {
                sendDistributorDocuments({
                    callback: afterSubmitCallback || (() => handleNextStep(true)),
                    payload: allDocuments,
                });
            }
        },
        [formIsValid, handleNextStep, sendDistributorDocuments, allDocuments]
    );

    useEffect(() => {
        if (replaceSuccess || createSuccess) {
            setLoadingDoc('');
        }
    }, [replaceSuccess, createSuccess]);

    const checkChanges = useCallback(() => false, []);

    useEffect(() => {
        if (formIsValid(false)) {
            setAllowSubmit(true);
        } else {
            setAllowSubmit(false);
        }
    }, [formIsValid]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, isResubmit),
        [handleSubmit, checkChanges, isResubmit]
    );

    return (
        <TunnelStep
            title={texts.DISTRIBUTOR_DOCUMENTS_TITLE}
            subtitle={texts.DISTRIBUTOR_DOCUMENTS_SUBTITLE}
        >
            <TunnelBox>
                <Loader loading={documentsLoader}>
                    {Object.keys(errors).find(key => errors[key]) && (
                        <ErrorMessage gutterBottom message={texts.FORM_HAS_ERRORS} />
                    )}
                    <Box>
                        {!distributorCompany?.legalRepresentant && (
                            <DocumentInput
                                loading={loadingDoc === 'legalRepresentantAuthority'}
                                name="legalRepresentantAuthority"
                                label={texts.LEGAL_REPRESENTANT_AUTHORITY_ALT}
                                doc={findDocument('legalRepresentantAuthority')}
                                onDocChange={handleUpload}
                                fileSizeError={fileSizeError}
                                error={errors.legalRepresentantAuthority}
                            />
                        )}
                        {role === RoleNames.ADMIN_CIF && (
                            <DocumentInput
                                loading={loadingDoc === 'oriasAttestation'}
                                name="oriasAttestation"
                                label={texts.ORIAS_ATTESTATION}
                                doc={findDocument('oriasAttestation')}
                                onDocChange={handleUpload}
                                fileSizeError={fileSizeError}
                                error={errors.oriasAttestation}
                            />
                        )}
                        {(role === RoleNames.ADMIN_CIF || role === RoleNames.PSI) && (
                            <DocumentInput
                                loading={loadingDoc === 'criminalRecordExtract'}
                                name="criminalRecordExtract"
                                label={texts.CRIMINAL_RECORD_EXTRACT}
                                doc={findDocument('criminalRecordExtract')}
                                onDocChange={handleUpload}
                                fileSizeError={fileSizeError}
                                error={errors.criminalRecordExtract}
                            />
                        )}
                        <DocumentInput
                            loading={loadingDoc === 'kbis'}
                            name="kbis"
                            label={texts.KBIS_RECENT}
                            doc={findDocument('kbis')}
                            onDocChange={handleUpload}
                            fileSizeError={fileSizeError}
                            error={errors.kbis}
                        />
                        <DocumentInput
                            loading={loadingDoc === 'insuranceCertificate'}
                            name="insuranceCertificate"
                            label={texts.INSURANCE_CERTIFICATE}
                            doc={findDocument('insuranceCertificate')}
                            onDocChange={handleUpload}
                            fileSizeError={fileSizeError}
                            error={errors.insuranceCertificate}
                        />
                        {!distributorCompany?.legalRepresentant && (
                            <DocumentInput
                                loading={loadingDoc === 'legalRepresentantId'}
                                name="legalRepresentantId"
                                label={texts.LEGAL_REPRESENTANT_ID}
                                doc={findDocument('legalRepresentantId')}
                                onDocChange={handleUpload}
                                fileSizeError={fileSizeError}
                                error={errors.legalRepresentantId}
                            />
                        )}
                        <DocumentInput
                            loading={loadingDoc === 'signatoryId'}
                            name="signatoryId"
                            label={texts.SIGNATORY_ID}
                            doc={findDocument('signatoryId')}
                            onDocChange={handleUpload}
                            fileSizeError={fileSizeError}
                            error={errors.signatoryId}
                            infoButtonMessage={
                                <React.Fragment>
                                    <Typography>{texts.ALLOWED_DOCUMENTS}</Typography>
                                    <ul>
                                        <li>
                                            <Typography>
                                                {texts.ALLOWED_DOCUMENTS_PASSPORT}
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography>{texts.ALLOWED_DOCUMENTS_ID}</Typography>
                                        </li>
                                        <li>
                                            <Typography>
                                                {texts.ALLOWED_DOCUMENTS_DRIVER_LICENCE}
                                            </Typography>
                                        </li>
                                    </ul>
                                </React.Fragment>
                            }
                        />
                        <DocumentInput
                            loading={loadingDoc === 'rib'}
                            name="rib"
                            label={texts.RIB_SOCIETE}
                            doc={findDocument('rib')}
                            onDocChange={handleUpload}
                            fileSizeError={fileSizeError}
                            error={errors.rib}
                        />
                        <DocumentInput
                            loading={loadingDoc === 'logo'}
                            name="logo"
                            label={texts.COMPANY_LOGO}
                            doc={findDocument('logo')}
                            onDocChange={handleUpload}
                            fileSizeError={fileSizeError}
                            error={errors.logo}
                            onlyImage
                        />
                        <Typography variant="caption" gutterBottom>
                            {texts.LOGO_ACCEPTED_FORMAT}
                        </Typography>
                        <Box marginTop="20px">
                            <UploadDocument
                                userId={internalUser?.id}
                                updateSubscription
                                hideSelect
                                withTitle
                                sx={{ marginBottom: 10 }}
                            />
                            {otherDocuments?.map(doc => (
                                <Box key={doc.id} display="flex" flexWrap="wrap" width="100%">
                                    <Document doc={doc} isRemovable />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Loader>
            </TunnelBox>
            <SubmitButton
                text={isStepVerified ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                onClick={isStepVerified ? handleNextStep : handleSubmit()}
                disabled={!allowSubmit || buttonLoader}
                loading={buttonLoader}
            />
        </TunnelStep>
    );
};

DistributorDocuments.propTypes = {
    userStep: PropTypes.object,
    handleNextStep: PropTypes.func,
};

export default DistributorDocuments;
